// Small presentational building blocks shared by admin and distributor pages.
import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopyOutlined";
import { figureSx, monoFont, statusColors } from "../theme";
import { STATUS_META } from "../lib/licenses";

export function PageHeader({ eyebrow, title, subtitle, actions }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "flex-end",
        justifyContent: "space-between",
        gap: 2,
        mb: 3,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        {eyebrow && (
          <Typography variant="overline" color="text.secondary" component="div">
            {eyebrow}
          </Typography>
        )}
        <Typography variant="h4" component="h1">
          {title}
        </Typography>
        {subtitle && (
          <Typography color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {actions && (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>{actions}</Box>
      )}
    </Box>
  );
}

export function SectionTitle({ children, action }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        mt: 4,
        mb: 1.5,
      }}
    >
      <Typography variant="h5" component="h2">
        {children}
      </Typography>
      {action}
    </Box>
  );
}

export function StatusChip({ status, size = "small" }) {
  const colors = statusColors[status];
  return (
    <Chip
      size={size}
      label={STATUS_META[status].label}
      sx={{
        bgcolor: colors.bg,
        color: colors.fg,
        fontWeight: 600,
        borderRadius: 1.5,
      }}
    />
  );
}

export function KeyText({ value, large = false, copy = true }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async (event) => {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (error) {
      console.error("Clipboard unavailable", error);
    }
  };
  return (
    <Box
      component="span"
      sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}
    >
      <Typography
        component="span"
        sx={{
          fontFamily: monoFont,
          fontSize: large ? { xs: "1.25rem", sm: "1.6rem" } : "0.875rem",
          fontWeight: large ? 600 : 500,
          letterSpacing: "0.04em",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </Typography>
      {copy && (
        <Tooltip title={copied ? "Copied" : "Copy key"}>
          <IconButton
            size="small"
            onClick={onCopy}
            aria-label="Copy license key"
          >
            {copied ? (
              <CheckIcon fontSize="inherit" color="success" />
            ) : (
              <ContentCopyIcon fontSize="inherit" />
            )}
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

export function StatCard({ label, value, hint, status, active, onClick }) {
  const accent = status ? statusColors[status].fg : undefined;
  const content = (
    <CardContent sx={{ py: 1.75, "&:last-child": { pb: 1.75 } }}>
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          display: "flex",
          alignItems: "center",
          gap: 0.75,
        }}
      >
        {accent && (
          <Box
            component="span"
            sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: accent }}
          />
        )}
        {label}
      </Typography>
      <Typography sx={{ ...figureSx, fontSize: "1.75rem", mt: 0.25 }}>
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      )}
    </CardContent>
  );
  return (
    <Card
      sx={{
        borderColor: active ? "text.primary" : undefined,
        boxShadow: active
          ? (t) => `inset 0 0 0 1px ${t.palette.text.primary}`
          : "none",
      }}
    >
      {onClick ? (
        <CardActionArea onClick={onClick}>{content}</CardActionArea>
      ) : (
        content
      )}
    </Card>
  );
}

export function StatGrid({ children, min = 150 }) {
  return (
    <Box
      sx={{
        display: "grid",
        gap: 1.5,
        gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))`,
      }}
    >
      {children}
    </Box>
  );
}

export function QuotaMeter({ allocated, issued }) {
  const value = allocated ? Math.min((issued / allocated) * 100, 100) : 0;
  return (
    <Box>
      <LinearProgress
        variant="determinate"
        value={value}
        color={value >= 90 ? "warning" : "primary"}
        aria-label={`${issued} of ${allocated} issued`}
      />
      <Typography variant="caption" color="text.secondary">
        {issued} of {allocated} issued
      </Typography>
    </Box>
  );
}

export function EmptyState({ title, body, action }) {
  return (
    <Box
      sx={{
        border: 1,
        borderStyle: "dashed",
        borderColor: "divider",
        borderRadius: 2,
        p: { xs: 3, sm: 5 },
        textAlign: "center",
        bgcolor: "background.paper",
      }}
    >
      <Typography variant="h5" component="p" gutterBottom>
        {title}
      </Typography>
      {body && (
        <Typography color="text.secondary" sx={{ maxWidth: 520, mx: "auto" }}>
          {body}
        </Typography>
      )}
      {action && <Box sx={{ mt: 2 }}>{action}</Box>}
    </Box>
  );
}

export function Loading({ label = "Loading…" }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        py: 6,
        justifyContent: "center",
      }}
    >
      <CircularProgress size={22} />
      <Typography color="text.secondary">{label}</Typography>
    </Box>
  );
}

export function Field({ label, children }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" component="div">
        {label}
      </Typography>
      <Typography
        variant="body2"
        component="div"
        sx={{ overflowWrap: "anywhere" }}
      >
        {children || "—"}
      </Typography>
    </Box>
  );
}

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = "Confirm",
  destructive = false,
  busy = false,
  onConfirm,
  onClose,
}) {
  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">{body}</DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          disabled={busy}
          variant="contained"
          color={destructive ? "error" : "primary"}
        >
          {busy ? "Working…" : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
